/**
 * Finding the URLs in a message.
 *
 * Kept free of JSX so it can be tested directly: this is the one place the app
 * turns someone else's text into something clickable, and what it *refuses*
 * matters more than what it matches.
 *
 * - Only `http://`, `https://` and a leading `www.` are recognised. A scheme
 *   the sender did not write is never invented, and `javascript:`, `data:` and
 *   friends cannot match at all — the danger in linkifying is handing someone a
 *   one-tap way to run something, and the cheapest defence is a pattern with no
 *   way to express it.
 * - The result is parsed before it is used, and anything whose protocol is not
 *   http or https after parsing is discarded. The pattern should make that
 *   unreachable; it is checked anyway.
 * - Trailing punctuation is not part of an address. "see https://a.com." ends
 *   in a full stop belonging to the sentence, and a closing bracket counts only
 *   if the address opened one.
 */

/** Stops at whitespace, angle brackets, quotes and the bidi isolate marks. */
const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"'`⁦-⁩]+/gi;

/** Punctuation that ends a sentence rather than an address. */
const TRAILING = /[.,;:!?'"’”]+$/;

export interface Link {
  /** Index into the original text. */
  start: number;
  /** The text the sender wrote, shown as-is. */
  raw: string;
  /** Where it actually goes, after parsing. */
  href: string;
}

function trimTrailing(match: string): string {
  let url = match.replace(TRAILING, '');
  for (const [open, close] of [
    ['(', ')'],
    ['[', ']'],
    ['{', '}'],
  ] as const) {
    while (url.endsWith(close) && url.split(open).length < url.split(close).length) {
      url = url.slice(0, -1);
    }
  }
  return url;
}

function hrefFor(url: string): string | undefined {
  const candidate = /^www\./i.test(url) ? `https://${url}` : url;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

/** Every address in `text`, in order, with the punctuation left behind. */
export function findLinks(text: string): Link[] {
  URL_PATTERN.lastIndex = 0;
  const links: Link[] = [];
  let match: RegExpExecArray | null;

  while ((match = URL_PATTERN.exec(text)) !== null) {
    const raw = trimTrailing(match[0]);
    const href = hrefFor(raw);
    if (href) links.push({ start: match.index, raw, href });
    // Whatever was trimmed is ordinary text and must not be skipped over.
    URL_PATTERN.lastIndex = match.index + raw.length;
  }

  return links;
}
